import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useShopifyProducts } from '../hooks/useShopifyProducts';

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  
  // Fetch ALL product data directly from Shopify
  const { products: shopifyProducts, loading } = useShopifyProducts();

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [priceRange, setPriceRange] = useState({
    min: searchParams.get('min_price') || '',
    max: searchParams.get('max_price') || ''
  });

  // Get unique categories from Shopify products
  const categories = useMemo(() => {
    const cats = [...new Set(shopifyProducts.map(p => p.category))];
    return cats.sort();
  }, [shopifyProducts]);

  // Filter products
  const filteredProducts = useMemo(() => {
    let filtered = [...shopifyProducts];

    if (selectedCategory) {
      filtered = filtered.filter(p => p.category.toLowerCase() === selectedCategory.toLowerCase());
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(query) || 
        p.description.toLowerCase().includes(query)
      );
    }

    if (priceRange.min) {
      filtered = filtered.filter(p => p.price >= parseFloat(priceRange.min));
    }

    if (priceRange.max) {
      filtered = filtered.filter(p => p.price <= parseFloat(priceRange.max));
    }

    return filtered;
  }, [shopifyProducts, selectedCategory, searchQuery, priceRange]);

  // Update URL params when filters change
  React.useEffect(() => {
    const params = new URLSearchParams();
    if (selectedCategory) params.set('category', selectedCategory);
    if (searchQuery) params.set('search', searchQuery);
    if (priceRange.min) params.set('min_price', priceRange.min);
    if (priceRange.max) params.set('max_price', priceRange.max);
    setSearchParams(params);
  }, [selectedCategory, searchQuery, priceRange, setSearchParams]);

  const clearFilters = () => {
    setSelectedCategory('');
    setSearchQuery('');
    setPriceRange({ min: '', max: '' });
  };

  const hasActiveFilters = selectedCategory || searchQuery || priceRange.min || priceRange.max;

  return (
    <div className="min-h-screen bg-white" data-testid="shop-page">
      {/* Header - Editorial, minimal */}
      <div className="bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
          <h1 
            className="font-handwritten"
            style={{
              fontSize: 'clamp(64px, 8vw, 96px)',
              color: '#1A1814',
              marginBottom: '12px',
              lineHeight: 1.0,
            }}
          >
            shop
          </h1>
          <p 
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: '12px',
              fontWeight: 400,
              color: '#555',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
            }}
          >
            {filteredProducts.length} PIECE{filteredProducts.length !== 1 ? 'S' : ''}
          </p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24">
        <div className="flex flex-col lg:flex-row gap-16">
          {/* Filters Sidebar - Minimal */}
          <aside className="hidden lg:block w-48 flex-shrink-0" data-testid="filters-sidebar">
            <div className="sticky top-32 space-y-12">
              {/* Search */}
              <div>
                <label 
                  className="block mb-4"
                  style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: '10px',
                    letterSpacing: '0.15em',
                    color: 'rgba(26,24,20,0.4)',
                  }}
                >
                  search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="search..."
                    className="w-full bg-transparent border-b border-plumo-black/20 px-0 py-2 font-body text-sm focus:border-plumo-black focus:outline-none transition-colors"
                    style={{ fontFamily: "'Space Mono', monospace", fontSize: '13px' }}
                    data-testid="search-input"
                  />
                  <Search className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-plumo-black/30" />
                </div>
              </div>

              {/* Categories */}
              <div>
                <label 
                  className="block mb-4"
                  style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: '10px',
                    letterSpacing: '0.15em',
                    color: 'rgba(26,24,20,0.4)',
                  }}
                >
                  category
                </label>
                <div className="space-y-3">
                  <button
                    onClick={() => setSelectedCategory('')}
                    className="block transition-colors"
                    style={{
                      fontFamily: "'Space Mono', monospace",
                      fontSize: '13px',
                      color: !selectedCategory ? '#1A1814' : 'rgba(26,24,20,0.5)',
                      borderBottom: !selectedCategory ? '1px solid #1A1814' : 'none',
                      paddingBottom: '2px',
                    }}
                    data-testid="filter-all"
                  >
                    all
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className="block transition-colors"
                      style={{
                        fontFamily: "'Space Mono', monospace",
                        fontSize: '13px',
                        color: selectedCategory === cat ? '#1A1814' : 'rgba(26,24,20,0.5)',
                        borderBottom: selectedCategory === cat ? '1px solid #1A1814' : 'none',
                        paddingBottom: '2px',
                      }}
                      data-testid={`filter-${cat}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div>
                <label 
                  className="block mb-4"
                  style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: '10px',
                    letterSpacing: '0.15em',
                    color: 'rgba(26,24,20,0.4)',
                  }}
                >
                  price
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    value={priceRange.min}
                    onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
                    placeholder="min"
                    className="w-full bg-transparent border-b border-plumo-black/20 px-0 py-2 focus:border-plumo-black focus:outline-none"
                    style={{ fontFamily: "'Space Mono', monospace", fontSize: '13px' }}
                    data-testid="price-min"
                  />
                  <span style={{ color: 'rgba(26,24,20,0.3)', fontSize: '12px' }}>—</span>
                  <input
                    type="number"
                    value={priceRange.max}
                    onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
                    placeholder="max"
                    className="w-full bg-transparent border-b border-plumo-black/20 px-0 py-2 focus:border-plumo-black focus:outline-none"
                    style={{ fontFamily: "'Space Mono', monospace", fontSize: '13px' }}
                    data-testid="price-max"
                  />
                </div>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: '11px',
                    color: 'rgba(26,24,20,0.4)',
                  }}
                  className="hover:text-plumo-black transition-colors"
                  data-testid="clear-filters"
                >
                  clear all
                </button>
              )}
            </div>
          </aside>

          {/* Mobile Filters Toggle */}
          <div className="lg:hidden flex gap-4 mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-plumo-black/10 font-body text-xs tracking-[0.1em]"
              data-testid="mobile-filters-toggle"
            >
              <SlidersHorizontal className="w-3 h-3" />
              filters
              {hasActiveFilters && (
                <span 
                  className="px-2 py-0.5 text-[10px]"
                  style={{ backgroundColor: '#C9A961', color: '#1A1814' }}
                >
                  active
                </span>
              )}
            </button>
          </div>

          {/* Mobile Filters Panel */}
          {showFilters && (
            <div className="lg:hidden fixed inset-0 bg-plumo-white z-50 overflow-y-auto" data-testid="mobile-filters-panel">
              <div className="p-6">
                <div className="flex justify-between items-center mb-8">
                  <h2 
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontWeight: 500,
                      fontStyle: 'italic',
                      fontSize: '24px',
                      color: '#1A1814',
                    }}
                  >
                    Filters
                  </h2>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="p-2"
                    data-testid="close-mobile-filters"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Mobile Search */}
                <div className="mb-8">
                  <label 
                    className="block mb-4"
                    style={{
                      fontFamily: "'Space Mono', monospace",
                      fontSize: '10px',
                      letterSpacing: '0.15em',
                      color: 'rgba(26,24,20,0.4)',
                    }}
                  >
                    search
                  </label>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="search..."
                    className="w-full bg-transparent border-b border-plumo-black/20 px-0 py-2 font-body text-sm focus:border-plumo-black focus:outline-none"
                    data-testid="mobile-search-input"
                  />
                </div>

                {/* Mobile Categories */}
                <div className="mb-8">
                  <label 
                    className="block mb-4"
                    style={{
                      fontFamily: "'Space Mono', monospace",
                      fontSize: '10px',
                      letterSpacing: '0.15em',
                      color: 'rgba(26,24,20,0.4)',
                    }}
                  >
                    category
                  </label>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => setSelectedCategory('')}
                      className="font-body text-sm px-3 py-1 transition-colors"
                      style={{
                        borderBottom: !selectedCategory ? '1px solid #1A1814' : 'none',
                        color: !selectedCategory ? '#1A1814' : 'rgba(26,24,20,0.5)',
                      }}
                    >
                      all
                    </button>
                    {categories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setSelectedCategory(cat)}
                        className="font-body text-sm px-3 py-1 transition-colors"
                        style={{
                          borderBottom: selectedCategory === cat ? '1px solid #1A1814' : 'none',
                          color: selectedCategory === cat ? '#1A1814' : 'rgba(26,24,20,0.5)',
                        }}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mobile Price Range */}
                <div className="mb-8">
                  <label 
                    className="block mb-4"
                    style={{
                      fontFamily: "'Space Mono', monospace",
                      fontSize: '10px',
                      letterSpacing: '0.15em',
                      color: 'rgba(26,24,20,0.4)',
                    }}
                  >
                    price
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="number"
                      value={priceRange.min}
                      onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
                      placeholder="min"
                      className="w-full bg-transparent border-b border-plumo-black/20 px-0 py-2 font-body text-sm"
                    />
                    <span style={{ color: 'rgba(26,24,20,0.3)' }}>—</span>
                    <input
                      type="number"
                      value={priceRange.max}
                      onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
                      placeholder="max"
                      className="w-full bg-transparent border-b border-plumo-black/20 px-0 py-2 font-body text-sm"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => setShowFilters(false)}
                    className="flex-1 px-6 py-3 font-body text-xs tracking-[0.1em]"
                    style={{ backgroundColor: '#1A1814', color: '#F5F0E8' }}
                    data-testid="apply-mobile-filters"
                  >
                    apply
                  </button>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="px-6 py-3 border border-plumo-black/10 font-body text-xs tracking-[0.1em]"
                    >
                      clear
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[4/5] bg-plumo-bone/50" />
                    <div className="pt-4 space-y-2">
                      <div className="h-4 bg-plumo-bone/50 w-3/4" />
                      <div className="h-3 bg-plumo-bone/50 w-1/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-20" data-testid="no-products">
                <p 
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontWeight: 500,
                    fontStyle: 'italic',
                    fontSize: '24px',
                    color: '#1A1814',
                    marginBottom: '12px',
                  }}
                >
                  No products found
                </p>
                <p 
                  style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: '13px',
                    color: 'rgba(26,24,20,0.5)',
                    marginBottom: '24px',
                  }}
                >
                  try adjusting your filters
                </p>
                <button
                  onClick={clearFilters}
                  style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: '12px',
                    color: 'rgba(26,24,20,0.5)',
                    textDecoration: 'underline',
                    textUnderlineOffset: '4px',
                  }}
                  className="hover:text-plumo-black transition-colors"
                  data-testid="clear-filters-empty"
                >
                  clear filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12" data-testid="products-grid">
                {filteredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
